import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MagnifyingGlass, Funnel } from '@phosphor-icons/react';
import { fetchProducts as fetchAllProducts } from '../lib/api';
import ProductCard from '../components/ProductCard';
import { CATEGORY_NAMES } from '../lib/categories';

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [loading, setLoading] = useState(true);

  const categories = ['All', ...CATEGORY_NAMES];

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProducts();
  }, []);

  // Keep selectedCategory in sync with URL changes (e.g., when navigating from
  // the Categories page to /shop?category=...)
  useEffect(() => {
    setSelectedCategory(searchParams.get('category') || 'All');
  }, [searchParams]);

  useEffect(() => {
    filterProducts();
  }, [products, searchQuery, selectedCategory, priceRange]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setProducts(await fetchAllProducts());
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    if (searchQuery) {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory !== 'All') {
      filtered = filtered.filter((product) => product.category === selectedCategory);
    }

    if (priceRange.min) {
      filtered = filtered.filter((product) => product.price >= parseFloat(priceRange.min));
    }

    if (priceRange.max) {
      filtered = filtered.filter((product) => product.price <= parseFloat(priceRange.max));
    }

    setFilteredProducts(filtered);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category !== 'All') {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 max-w-7xl mx-auto" data-testid="shop-page">
      {selectedCategory !== 'All' ? (
        <div className="mb-10">
          <Link
            to="/categories"
            className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
            data-testid="all-categories-link"
          >
            Categories / {selectedCategory}
          </Link>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight mt-2">
            {selectedCategory} Posters
          </h1>
          <p className="text-muted-foreground mt-2 text-sm" data-testid="category-count">
            {loading ? 'Loading…' : `${filteredProducts.length} poster${filteredProducts.length === 1 ? '' : 's'}`}
          </p>
        </div>
      ) : (
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight mb-12">
          Shop All Posters
        </h1>
      )}

      {/* Filters Section */}
      <div className="mb-12 space-y-6">
        {/* Search Bar */}
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Search posters..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-secondary border border-border/40 rounded-sm px-4 py-3 pl-12 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-white/50 transition-colors"
            data-testid="search-input"
          />
          <MagnifyingGlass
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Funnel size={18} />
            <span className="text-sm font-medium">Category:</span>
          </div>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-white text-black'
                  : 'bg-secondary text-foreground/80 hover:bg-secondary/80 border border-border/40'
              }`}
              data-testid={`filter-${category.toLowerCase()}`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Price Range */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">Price Range:</span>
          <input
            type="number"
            placeholder="Min"
            value={priceRange.min}
            onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
            className="w-24 bg-secondary border border-border/40 rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-white/50 transition-colors"
            data-testid="price-min-input"
          />
          <span className="text-muted-foreground">-</span>
          <input
            type="number"
            placeholder="Max"
            value={priceRange.max}
            onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
            className="w-24 bg-secondary border border-border/40 rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-white/50 transition-colors"
            data-testid="price-max-input"
          />
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="text-center py-20 text-muted-foreground">Loading products...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-muted-foreground text-lg">No products found matching your criteria.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6 grid-flow-row-dense" data-testid="products-grid">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Shop;
